import './index.scss';

import { AnchorHTMLAttributes, Component, DetailedHTMLProps, LiHTMLAttributes, ReactNode } from 'react';
import Sortable from 'sortablejs';

import { BraxThemeVariant } from '../../types';
import { BraxIcon, BraxIconName } from '../BraxIcon';

export type BraxListItemVariant = Extract<BraxThemeVariant, 'primary' | 'secondary' | 'light' | 'success' | 'destructive'>;

export type BraxListItem = {
    key?: string,
    icon?: BraxIconName | ReactNode,
    title: ReactNode,
    description?: ReactNode,
    link?: string | AnchorHTMLAttributes<HTMLAnchorElement>,
    variant?: BraxListItemVariant,
    hidden?: boolean,
    disabled?: boolean,
    onSelect?: () => void,
    onIconClick?: () => void
};

export type BraxListProps = {
    items: BraxListItem[],
    type: 'unordered' | 'ordered',
    variant: BraxListItemVariant,
    sortable: boolean,
    listItemProps: DetailedHTMLProps<LiHTMLAttributes<HTMLLIElement>, HTMLLIElement> | ((item: BraxListItem, index: number) => DetailedHTMLProps<LiHTMLAttributes<HTMLLIElement>, HTMLLIElement>)
};

export class BraxList extends Component<BraxListProps> {

    static defaultProps = {
        type: 'unordered',
        variant: 'light',
        sortable: false,
        listItemProps: {}
    };

    element: HTMLElement | null = null;

    sortableInstance: Sortable | null = null;

    iconClicked = false;

    sortHandleClicked = false;

    componentDidMount() {
        this.startSortable();
    }

    componentWillUnmount() {
        if (this.sortableInstance) {
            this.sortableInstance.destroy();

            this.sortableInstance = null;
        }
    }

    setRef(element: HTMLElement | null) {
        this.element = element;

        this.startSortable();
    }

    startSortable() {
        if (this.props.sortable && this.element && !this.sortableInstance)
            this.sortableInstance = new Sortable(this.element, {
                handle: '.sort',
                animation: 150
            });
    }

    select(item: BraxListItem) {
        if (this.sortHandleClicked === false && this.iconClicked === false && !item.disabled)
            item.onSelect?.();

        this.iconClicked = false;

        this.sortHandleClicked = false;
    }

    onIconClick() {
        this.iconClicked = true;
    }

    onSortHandleClick() {
        this.sortHandleClicked = true;
    }

    render() {
        // const CustomComponent = forwardRef<HTMLDivElement, any>((props, ref) => {
        //     return <div ref={ref}>{props.children}</div>;
        // });
        // <ReactSortable tag={CustomComponent} list={this.props.items} setList={() => this.order()}>
        //     {/* { this.props.items.map((item) => () } */}
        // </ReactSortable>

        const items = this.props.items.map((item, index) => {
            if (item.hidden)
                return null;

            const { key, className, onClick, ...props } = typeof this.props.listItemProps === 'function' ? this.props.listItemProps(item, index) : this.props.listItemProps;

            const content = (<>
                { this.props.sortable || item.icon ? (
                    <div className="icons">
                        { this.props.sortable ? (
                            <BraxIcon onClick={() => this.onSortHandleClick()} icon="six-dots" className="sort" />
                        ) : null }

                        { typeof item.icon === 'string' ? (
                            <BraxIcon icon={item.icon} onClick={() => { this.onIconClick(); item.onIconClick?.(); }} />
                        ) : (
                            item.icon ?? null
                        ) }
                    </div>
                ) : null }

                <div>
                    <p>{ item.title }</p>

                    { item.description ? (
                        <p className="hint">{ item.description }</p>
                    ) : null }
                </div>
            </>);

            return (
                <li
                    key={key ?? item.key ?? index}
                    onClick={event => {
                        this.select(item);

                        onClick?.(event);
                    }}
                    className={`${!item.disabled && !!item.onSelect ? 'clickable' : ''} ${item.variant ?? this.props.variant ?? ''} ${item.disabled ? 'disabled' : ''} ${className || ''}`}
                    {...props}
                >
                    { item.link ? (
                        <a {...(typeof item.link === 'string' ? { href: item.link } : item.link)}>{ content }</a>
                    ) : (
                        <div>{ content }</div>
                    ) }
                </li>
            );
        });

        return this.props.type === 'ordered' ? (
            <ol ref={element => this.setRef(element)} className="brax-list">{ items }</ol>
        ) : (
            <ul ref={element => this.setRef(element)} className="brax-list">{ items }</ul>
        );
    }

}
